import { inject, Injectable } from '@angular/core';
import { Menu, MenuService, MenuChildrenItem } from './menu.service';
import { AuthService } from './auth.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class StartupService {
  private authService = inject(AuthService);
  private permissionsService = inject(NgxPermissionsService);
  private menuService = inject(MenuService);
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  /**
   * Load menu and permissions at app startup.
   */
  async load(): Promise<void> {

    let menu: Menu[] | undefined;
    if (this.authService.isAuthenticated()) {
      try {
        const resp = await lastValueFrom(this.http.get<{ menu?: Menu[] }>('assets/data/menu.json'));
        menu = resp?.menu ?? (resp as unknown as Menu[]);
      } catch {
        try {
          const assetResp = await lastValueFrom(this.http.get<{ menu?: Menu[] }>('assets/data/menu.json'));
          menu = assetResp?.menu ?? (assetResp as unknown as Menu[]);
        } catch {
          menu = undefined;
        }
      }
    } else {
      try {

        const assetResp = await lastValueFrom(this.http.get<{ menu?: Menu[] }>('assets/data/menu.json'));
        menu = assetResp?.menu ?? (assetResp as unknown as Menu[]);
      } catch {

        menu = undefined;
      }
    }


    if (menu) {
      const user = this.authService.getCurrentUser();
      let finalMenu = menu;
      if (user && user.roles && user.roles.length > 0) {
        finalMenu = this.filterMenuByRoles(menu, user.roles);
        try {
          this.permissionsService.flushPermissions();
          // Load both roles (for menu visibility) and permissions (for feature cards)
          const grants = [...user.roles, ...(user.permissions ?? [])];
          this.permissionsService.loadPermissions(grants);
        } catch { /* empty */ }
      }

      console.log("Final menu:", finalMenu);
      this.setMenu(finalMenu);
    }
  }
    private setMenu(menu: Menu[]) {
        const stripPrefix = (items: Menu[]): Menu[] =>
            items.map((item) => {
                const newItem: Menu = { ...(item as Menu) };
                if (newItem.name.startsWith('menu.')) {
                    newItem.name = newItem.name.replace(/^menu\./, '');
                }
                if (newItem.children && newItem.children.length > 0) {
                    newItem.children = stripPrefix(newItem.children as Menu[]);
                }
                return newItem;
            });

        const cleanedMenu = stripPrefix(menu);
        this.menuService.set(cleanedMenu);
    }

  private filterMenuByRoles(menu: Menu[] | MenuChildrenItem[], roles: string[]): Menu[] {
    const filtered: Menu[] = [];

    for (const item of menu as Menu[]) {
      if (!this.hasAccess(item as Menu, roles)) continue;

      const newItem: Menu = { ...(item as Menu) };

      if (newItem.children && newItem.children.length > 0) {
        const children = this.filterMenuByRoles(newItem.children, roles) as MenuChildrenItem[];
        newItem.children = children;
        if (children.length === 0 && (!newItem.route || newItem.route === '')) {
          continue;
        }
      }

      filtered.push(newItem as Menu);
    }

    return filtered;
  }

  private hasAccess(item: Menu | MenuChildrenItem, roles: string[]): boolean {
    const perms = (item as Menu).roles;
    if (!perms) return true;

    const only = perms.only ? (Array.isArray(perms.only) ? perms.only : [perms.only]) : undefined;
    const except = perms.except ? (Array.isArray(perms.except) ? perms.except : [perms.except]) : undefined;

    if (only && only.length > 0) {
      return roles.some((r) => only.includes(r));
    }

    if (except && except.length > 0) {
      return !roles.some((r) => except.includes(r));
    }

    return true;
  }
}
